import { Component, OnInit, OnDestroy, computed, signal, Signal, Input, Output, EventEmitter, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NbInputModule, NbSelectModule, NbOptionModule } from '@nebular/theme';
import { TranslateModule } from '@ngx-translate/core';
import { KPI } from '../../../@core/api';
import { ScopeService } from '../../../@core/utils';
import { idMapToArray } from '../../../@core/utils/scope.service';
import { formatSearchString } from '../../../@core/utils/utils.service';
import { Subscription, combineLatest, Subject } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';

export interface AssetTypeWithHierarchy extends KPI.AssetType {
  hierarchyPath: string;
  super_category?: KPI.SuperCategory;
  category?: KPI.Category;
  sub_category?: KPI.SubCategory;
  name: string;
  matchingField?: number;
}

class SearchResult {
  constructor(
    public assetType: KPI.AssetType,
    public subCategory: KPI.SubCategory,
    public category: KPI.Category,
    public matchingField?: number
  ) {}
}

enum matchingField {
  "assetType" = 1,
  "keyword" = 2,
  "subCategory" = 3,
  "category" = 4,
}

@Component({
  selector: 'app-asset-type-selector',
  templateUrl: './asset-type-selector.component.html',
  styleUrls: ['./asset-type-selector.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbInputModule,
    NbSelectModule,
    NbOptionModule,
    TranslateModule,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf
  ]
})
export class AssetTypeSelectorComponent implements OnInit, OnDestroy {
  @Input() public searchMode: boolean = false;
  @Input() public editMode: boolean = true;
  @Input() public parent: KPI.Asset | null = null;
  @Input() public autoSelectAssetTypeId: number = 0;
  @Input() public autoSelectSubCategoryId: number = 0;
  @Input() public autoSelectCategoryId: number = 0;
  @Input() public preventAssetTypesThatNeedParent: boolean = false;

  @Output() public assetTypeSelected = new EventEmitter<AssetTypeWithHierarchy>();

  public searchQuery = signal<string>('');
  private searchSubject = new Subject<string>();
  public availableCategories: KPI.Category[] = [];
  public availableSubCategories: KPI.SubCategory[] = [];
  public availableAssetTypes: AssetTypeWithHierarchy[] = [];
  public searchResults: SearchResult[] = [];
  
  public selectedSuperCategory: WritableSignal<number | null> = signal(null);
  public selectedCategory: WritableSignal<number | null> = signal(null);
  public selectedSubCategory: WritableSignal<number | null> = signal(null);
  public selectedAssetTypeId: WritableSignal<number | null> = signal(null);

  public superCategories: Signal<KPI.SuperCategory[]> = toSignal(
    this.scopeService.superCategories$.pipe(idMapToArray())
  );
  public categories: Signal<KPI.Category[]> = toSignal(
    this.scopeService.categories$.pipe(idMapToArray())
  );
  public subCategories: Signal<KPI.SubCategory[]> = toSignal(
    this.scopeService.subCategories$.pipe(idMapToArray())
  );
  public assetTypes: Signal<KPI.AssetType[]> = toSignal(
    this.scopeService.assetTypes$.pipe(idMapToArray())
  );

  public filteredCategories: Signal<KPI.Category[]> = computed(() => {
    if (this.parent && this.availableCategories.length > 0) {
      return this.availableCategories;
    }
    const superCatId = this.selectedSuperCategory();
    if (!superCatId || !this.categories()) return [];
    return this.categories().filter(cat => cat.parent === superCatId);
  });

  public filteredSubCategories: Signal<KPI.SubCategory[]> = computed(() => {
    if (this.parent && this.availableSubCategories.length > 0) {
      return this.availableSubCategories;
    }
    const catId = this.selectedCategory();
    if (!catId || !this.subCategories()) return [];
    return this.subCategories().filter(subCat => subCat.parent === catId);
  });

  public filteredAssetTypesByHierarchy: Signal<AssetTypeWithHierarchy[]> = computed(() => {
    if (this.parent && this.availableAssetTypes.length > 0) {
      return this.availableAssetTypes;
    }
    const subCatId = this.selectedSubCategory();
    if (!subCatId || !this.assetTypes()) return [];
    
    return this.assetTypes()
      .filter(assetType => {
        if (assetType.parent !== subCatId) return false;
        return this.isAssetTypeAllowed(assetType);
      })
      .map(assetType => {
        const sub_category = this.subCategories()?.find(sc => sc.id === assetType.parent);
        const category = sub_category ? this.categories()?.find(c => c.id === sub_category.parent) : null;
        const super_category = category ? this.superCategories()?.find(sc => sc.id === category.parent) : null;

        const pathParts: string[] = [];
        if (super_category) pathParts.push(super_category.name);
        if (category) pathParts.push(category.name);
        if (sub_category) pathParts.push(sub_category.name);

        return {
          ...assetType,
          super_category,
          category,
          sub_category,
          hierarchyPath: pathParts.join(' > ')
        } as AssetTypeWithHierarchy;
      });
  });

  public filteredAssetTypes: Signal<AssetTypeWithHierarchy[]> = computed(() => {
    const searchText = this.searchQuery();
          
    if (!searchText || searchText.trim() === "") {
    const filtered = this.filteredAssetTypesByHierarchy();
    if (filtered.length === 1 && !this.selectedAssetTypeId()) {
      setTimeout(() => {
        this.selectAssetTypeFromDropdown(filtered[0].id);
      }, 0);
    }
    return filtered;
    }

    return this.searchResults.map(result => {
      const pathParts: string[] = [];
      if (result.category && result.category.name) pathParts.push(result.category.name);
      if (result.subCategory && result.subCategory.name) pathParts.push(result.subCategory.name);

      return {
        ...result.assetType,
        category: result.category,
        sub_category: result.subCategory,
        hierarchyPath: pathParts.join(' > '),
        matchingField: result.matchingField
      } as AssetTypeWithHierarchy;
    });
  });

  private subscriptions: Subscription[] = [];

  constructor(
    private scopeService: ScopeService
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.searchSubject.pipe(
        debounceTime(500),
        distinctUntilChanged()
      ).subscribe(query => {
        this.searchQuery.set(query);
        this.onSearchChanged();
      })
    );

    this.loadAssetTypes();
    
    if (this.parent) {
      this.loadParentAssetTypes();
    } else {
      this.subscriptions.push(
        combineLatest({
          categories: this.scopeService.categories$.pipe(idMapToArray()),
          subCategories: this.scopeService.subCategories$.pipe(idMapToArray()),
          assetTypes: this.scopeService.assetTypes$.pipe(idMapToArray())
        }).subscribe(({ categories, subCategories, assetTypes }) => {
          if (this.autoSelectAssetTypeId) {
            this.autoSelectAssetType(categories, subCategories, assetTypes, this.autoSelectAssetTypeId);
          } else if (this.autoSelectSubCategoryId) {
            this.autoSelectSubCategory(categories, subCategories, this.autoSelectSubCategoryId);
          } else if (this.autoSelectCategoryId) {
            this.autoSelectCategory(categories, this.autoSelectCategoryId);
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private loadAssetTypes(): void {
    this.subscriptions.push(
      combineLatest({
        categories: this.scopeService.categories$.pipe(idMapToArray()),
        subCategories: this.scopeService.subCategories$.pipe(idMapToArray()),
        assetTypes: this.scopeService.assetTypes$.pipe(idMapToArray())
      }).pipe(
        map(({ categories, subCategories, assetTypes }) => {
          this.availableCategories = categories.map(category => {
            const categorySubCategories = subCategories.filter(sc => sc.parent === category.id);
            const subCategoriesWithChildren = categorySubCategories.map(subCat => {
              const subCatAssetTypes = assetTypes.filter(at => at.parent === subCat.id);
              return {
                ...subCat,
                children: subCatAssetTypes
              };
            });
            return {
              ...category,
              children: subCategoriesWithChildren
            };
          });
        })
      ).subscribe()
    );
  }

  public onSearchChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  public onSearchChanged(event?: Event): void {
    this.searchMode = true;
    let formattedSearchText: string;

    if (event) {
      const input = event.target as HTMLInputElement;
      this.searchQuery.set(input.value);
    }

    const searchText = this.searchQuery();
    if (searchText && searchText !== "") {
      formattedSearchText = formatSearchString(searchText);
    } else {
      formattedSearchText = "";
    }

    let unsortedResults = this.availableCategories.reduce((matching: SearchResult[], elt: any) => {
      let subCategoryResults = this.getSubCategoriesSearchResults(elt, formattedSearchText);
      if (subCategoryResults.length > 0) {
        matching = matching.concat(subCategoryResults);
      }
      return matching;
    }, []);

    this.searchResults = this.sortSearchResults(unsortedResults, formattedSearchText);
  }

  private getSubCategoriesSearchResults(category: any, searchText: string): SearchResult[] {
    return category.children.reduce((matching: SearchResult[], elt: any) => {
      let assetTypesResults = this.getAssetTypesSearchResults(category, elt, searchText);
      if (assetTypesResults.length > 0) {
        matching = matching.concat(assetTypesResults);
      }
      return matching;
    }, []);
  }

  private getAssetTypesSearchResults(category: any, subCategory: any, searchText: string): SearchResult[] {
    return subCategory.children.reduce((matching: SearchResult[], assetType: KPI.AssetType) => {
      if (searchText === "") {
        matching.push(new SearchResult(assetType, subCategory, category));
      } else if (formatSearchString(assetType.name).includes(searchText)) {
        matching.push(new SearchResult(assetType, subCategory, category, matchingField.assetType));
      } else if (formatSearchString(subCategory.name).includes(searchText)) {
        matching.push(new SearchResult(assetType, subCategory, category, matchingField.subCategory));
      } else if (formatSearchString(category.name).includes(searchText)) {
        matching.push(new SearchResult(assetType, subCategory, category, matchingField.category));
      } else if (this.doKeywordsMatch(assetType, searchText)) {
        matching.push(new SearchResult(assetType, subCategory, category, matchingField.keyword));
      }

      return matching;
    }, []);
  }

  public clearSearch(): void {
    this.searchQuery.set('');
    this.searchSubject.next('');
  }

  public onSearchCancelled(): void {}

  public getFormattedHierarchyPath(assetType: AssetTypeWithHierarchy): string {
    const parts: string[] = [];
    
    if (assetType.category) {
      parts.push(assetType.category.name);
    }
    if (assetType.sub_category) {
      parts.push(assetType.sub_category.name);
    }
    
    return parts.join(' > ');
  }

  private sortSearchResults(results: SearchResult[], searchText: string): SearchResult[] {
    return results.sort((a, b) => {
      if (a.matchingField === b.matchingField) {
        if (a.matchingField === matchingField.assetType) {
          if (
            formatSearchString(a.assetType.name).indexOf(searchText) === 0 &&
            formatSearchString(b.assetType.name).indexOf(searchText) > 0
          ) {
            return -1;
          }
          if (
            formatSearchString(a.assetType.name).indexOf(searchText) > 0 &&
            formatSearchString(b.assetType.name).indexOf(searchText) === 0
          ) {
            return 1;
          }
          if (formatSearchString(a.assetType.name) < formatSearchString(b.assetType.name)) {
            return -1;
          }
          if (formatSearchString(a.assetType.name) > formatSearchString(b.assetType.name)) {
            return 1;
          }
          return 0;
        } else {
          if (formatSearchString(a.assetType.name) < formatSearchString(b.assetType.name)) {
            return -1;
          }
          if (formatSearchString(a.assetType.name) > formatSearchString(b.assetType.name)) {
            return 1;
          }
          return 0;
        }
      } else {
        if (a.matchingField < b.matchingField) {
          return -1;
        }
        if (a.matchingField > b.matchingField) {
          return 1;
        }
        return 0;
      }
    });
  }

  public selectSuperCategory(superCategoryId: number | null): void {
    this.selectedSuperCategory.set(superCategoryId);
    this.selectedCategory.set(null);
    this.selectedSubCategory.set(null);
  }

  public selectCategory(categoryId: number | null): void {
    this.selectedCategory.set(categoryId);
    this.selectedSubCategory.set(null);
  }

  public selectSubCategory(subCategoryId: number | null): void {
    this.selectedSubCategory.set(subCategoryId);
    this.selectedAssetTypeId.set(null);
    
    setTimeout(() => {
      const filtered = this.filteredAssetTypesByHierarchy();
      if (filtered.length === 1) {
        this.selectAssetTypeFromDropdown(filtered[0].id);
      }
    }, 0);
  }

  public selectAssetType(assetType: AssetTypeWithHierarchy): void {
    if (!this.isAssetTypeAllowed(assetType)) {
      return;
    }
    this.assetTypeSelected.emit(assetType);
  }

  public selectAssetTypeFromDropdown(assetTypeId: number | null): void {
    if (!assetTypeId) {
      this.selectedAssetTypeId.set(null);
      return;
    }
    
    const assetType = this.filteredAssetTypes().find(at => at.id === assetTypeId);
    if (assetType) {
      this.selectAssetType(assetType);
    }
  }


  public trackByAssetTypeId(_index: number, assetType: AssetTypeWithHierarchy): number {
    return assetType.id;
  }


  private doKeywordsMatch(assetType: KPI.AssetType, searchText: string): boolean {
    let matchingKeyword = (assetType as any).keywords.find((keyword: any) => formatSearchString(keyword.name).includes(searchText));
    return !!matchingKeyword;
  }

  private needsParent(assetType: KPI.AssetType): boolean {
    return (
      assetType.level === KPI.AssetTypeLevel.COLLECTION_ITEM ||
      assetType.level === KPI.AssetTypeLevel.COMPONENT
    );
  }

  private isAssetTypeAllowed(assetType: KPI.AssetType): boolean {
    if (this.preventAssetTypesThatNeedParent && this.needsParent(assetType)) {
      return false;
    }
    return true;
  }

  private loadParentAssetTypes(): void {
    if (!this.parent || !this.parent.asset_type) return;
    
    this.subscriptions.push(
      combineLatest({
        superCategories: this.scopeService.superCategories$.pipe(idMapToArray()),
        categories: this.scopeService.categories$.pipe(idMapToArray()),
        subCategories: this.scopeService.subCategories$.pipe(idMapToArray()),
        assetTypes: this.scopeService.assetTypes$.pipe(idMapToArray())
      }).subscribe(({ superCategories, categories, subCategories, assetTypes }) => {
        const parentAssetType = assetTypes.find(at => at.id === this.parent!.asset_type);
        if (!parentAssetType) return;

        const subCategory = subCategories.find(sc => sc.id === parentAssetType.parent);
        if (!subCategory) return;

        const category = categories.find(c => c.id === subCategory.parent);
        if (!category) return;

        const superCategory = superCategories.find(sc => sc.id === category.parent);
        if (!superCategory) return;

        const childAssetTypes = assetTypes
          .filter(at => at.parent === subCategory.id && this.isAssetTypeAllowed(at))
          .map(assetType => ({
            ...assetType,
            super_category: superCategory,
            category: category,
            sub_category: subCategory,
            hierarchyPath: [superCategory.name, category.name, subCategory.name].join(' > ')
          } as AssetTypeWithHierarchy));

        this.availableCategories = [category];
        this.availableSubCategories = [subCategory];
        this.availableAssetTypes = childAssetTypes;

        if (childAssetTypes.length === 1) {
          this.selectedSuperCategory.set(superCategory.id);
          this.selectedCategory.set(category.id);
          this.selectedSubCategory.set(subCategory.id);
          setTimeout(() => {
            this.selectAssetTypeFromDropdown(childAssetTypes[0].id);
          }, 0);
        } else {
          this.selectedSuperCategory.set(superCategory.id);
          this.selectedCategory.set(category.id);
          this.selectedSubCategory.set(subCategory.id);
        }
      })
    );
  }

  private autoSelectAssetType(
    categories: KPI.Category[],
    subCategories: KPI.SubCategory[],
    assetTypes: KPI.AssetType[],
    assetTypeId: number
  ): void {
    for (const category of categories) {
      for (const subCategory of subCategories.filter(sc => sc.parent === category.id)) {
        for (const assetType of assetTypes.filter(at => at.parent === subCategory.id)) {
          if (assetType.id === assetTypeId) {
            const superCategory = this.superCategories()?.find(sc => sc.id === category.parent);
            if (superCategory) {
              this.selectedSuperCategory.set(superCategory.id);
            }
            this.selectedCategory.set(category.id);
            this.selectedSubCategory.set(subCategory.id);
            this.selectedAssetTypeId.set(assetType.id);
            return;
          }
        }
      }
    }
  }

  private autoSelectSubCategory(
    categories: KPI.Category[],
    subCategories: KPI.SubCategory[],
    subCategoryId: number
  ): void {
    for (const category of categories) {
      const subCategory = subCategories.find(sc => sc.id === subCategoryId && sc.parent === category.id);
      if (subCategory) {
        const superCategory = this.superCategories()?.find(sc => sc.id === category.parent);
        if (superCategory) {
          this.selectedSuperCategory.set(superCategory.id);
        }
        this.selectedCategory.set(category.id);
        this.selectedSubCategory.set(subCategory.id);
        return;
      }
    }
  }

  private autoSelectCategory(categories: KPI.Category[], categoryId: number): void {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      const superCategory = this.superCategories()?.find(sc => sc.id === category.parent);
      if (superCategory) {
        this.selectedSuperCategory.set(superCategory.id);
      }
      this.selectedCategory.set(category.id);
    }
  }
}

